import { AnalyticsLogger } from "@oai/analytics";

import {
  getClientEventsServiceLogEventUrl,
  getClientEventsServiceProtocol,
} from "@/lib/clientEventsServiceConfig";
import { getOrSetDeviceId } from "@/lib/deviceId";

import type { StatsigClient } from "@statsig/react-bindings";
import type { MessageFns, MessagePayload, NoExtraKeys } from "@oai/analytics/analytics-logger";
import type { SofaUser } from "@/hooks/useGlobalContext";

type TapestryStatsigClient = Pick<StatsigClient, "flush" | "getContext" | "logEvent">;

const APP_VERSION = import.meta.env.VITE_APP_RELEASE || "unknown";
const DEFAULT_LOCALE = "en-US";
const CLIENT_EVENTS_SERVICE_DOMAIN: string | undefined = import.meta.env
  .CLIENT_EVENTS_SERVICE_DOMAIN;
const ENVIRONMENT = import.meta.env.VITE_ENV ?? "development";
const CLIENT_EVENTS_SERVICE_PROTOCOL = getClientEventsServiceProtocol(ENVIRONMENT);
export const CLIENT_EVENTS_SERVICE_LOG_EVENT_URL = getClientEventsServiceLogEventUrl(
  CLIENT_EVENTS_SERVICE_DOMAIN,
  ENVIRONMENT,
);

const CLIENT_EVENTS_SERVICE_SETTINGS =
  CLIENT_EVENTS_SERVICE_DOMAIN ?
    {
      writeKey: "oai",
      cdnURL: `${CLIENT_EVENTS_SERVICE_PROTOCOL}://${CLIENT_EVENTS_SERVICE_DOMAIN}`,
    }
  : null;

const CLIENT_EVENTS_SERVICE_INIT_OPTIONS =
  CLIENT_EVENTS_SERVICE_DOMAIN ?
    {
      disableClientPersistence: true,
      integrations: {
        "Segment.io": {
          apiHost: `${CLIENT_EVENTS_SERVICE_DOMAIN}/v1`,
          protocol: CLIENT_EVENTS_SERVICE_PROTOCOL,
        },
      },
    }
  : null;

let analyticsLogger: AnalyticsLogger | null = null;
let initializedUserId: string | null = null;
let initializedStatsigClient: TapestryStatsigClient | undefined;

function getAnalyticsLogger(): AnalyticsLogger | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (!CLIENT_EVENTS_SERVICE_SETTINGS || !CLIENT_EVENTS_SERVICE_INIT_OPTIONS) {
    return null;
  }

  if (analyticsLogger) {
    return analyticsLogger;
  }

  analyticsLogger = new AnalyticsLogger({
    appName: "tapestry",
    appVersion: APP_VERSION,
    deviceId: getOrSetDeviceId(),
    browserLocale: navigator.language || DEFAULT_LOCALE,
    options: CLIENT_EVENTS_SERVICE_INIT_OPTIONS,
    settings: CLIENT_EVENTS_SERVICE_SETTINGS,
  });

  return analyticsLogger;
}

export async function initializeAnalytics(user?: SofaUser, statsigClient?: TapestryStatsigClient) {
  const logger = getAnalyticsLogger();
  const nextUserId = user?.id ?? null;
  const nextStatsigClient = statsigClient;

  if (
    !logger ||
    (initializedUserId === nextUserId && initializedStatsigClient === nextStatsigClient)
  ) {
    return;
  }

  initializedUserId = nextUserId;
  initializedStatsigClient = nextStatsigClient;
  await logger.initialize({
    user:
      user ?
        {
          userId: user.id,
          traits: {},
        }
      : undefined,
    statsigClient:
      statsigClient ?
        ({
          getContext: statsigClient.getContext.bind(statsigClient),
          logEvent: statsigClient.logEvent.bind(statsigClient),
          flush: statsigClient.flush.bind(statsigClient),
          get networkConfig() {
            return { logEventUrl: CLIENT_EVENTS_SERVICE_LOG_EVENT_URL };
          },
        } as unknown as NonNullable<Parameters<AnalyticsLogger["initialize"]>[0]["statsigClient"]>)
      : undefined,
  });
}

export function logStructuredEvent<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  M extends MessageFns<any, string>,
  Payload extends MessagePayload<M>,
>(messageType: M, payload: NoExtraKeys<Payload, MessagePayload<M>>) {
  const logger = getAnalyticsLogger();
  if (!logger) {
    return;
  }

  return logger.trackStructuredEventAndReturnId(messageType, payload);
}
